import React, { useState, useEffect } from "react";
import api from "../../api/api";


// Layer 3 System Mode Badge
const layer3Config = {
  FULL_ACTIVE: { label: "Full Active",  color: "var(--success)", bg: "#10b98115", icon: "●" },
  MONITORING:  { label: "Monitoring",   color: "var(--warning)", bg: "#f59e0b15", icon: "●" },
  DEFENSIVE:   { label: "Defensive",    color: "var(--warning)", bg: "#f9731615", icon: "●" },
  PROTECTED:   { label: "Protected",    color: "var(--danger)", bg: "#ef444415", icon: "●" },
};

function Layer3Badge({ mode, reasons, multiplier }) {
  const [show, setShow] = React.useState(false);
  const cfg = layer3Config[mode] || layer3Config.FULL_ACTIVE;
  const pct = multiplier ? Math.round(multiplier * 100) : 100;
  let softReasons = [];
  try { softReasons = typeof reasons === "string" ? JSON.parse(reasons) : (reasons || []); } catch(e) {}
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <span
        onClick={() => setShow(!show)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "2px 8px", borderRadius: 20,
          background: cfg.bg, color: cfg.color,
          fontSize: 11, fontWeight: 700, cursor: "pointer",
          border: `1px solid ${cfg.color}33`
        }}
      >
        <span style={{ fontSize: 8 }}>{cfg.icon}</span>
        {cfg.label}
        {pct < 100 && <span style={{ fontSize: 10, opacity: 0.8 }}>{pct}%</span>}
      </span>
      {show && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 100,
          background: "var(--color-background-primary, #fff)",
          border: "1px solid var(--color-border-tertiary, #e5e7eb)",
          borderRadius: 10, padding: "12px 14px", minWidth: 220,
          boxShadow: "0 4px 16px rgba(0,0,0,0.10)"
        }}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: cfg.color }}>
            System Mode: {cfg.label}
          </div>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary, #6b7280)", marginBottom: 8 }}>
            Position size adjusted to <b>{pct}%</b> of normal
          </div>
          {softReasons.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary, #6b7280)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Signals detected</div>
              {softReasons.map((r, i) => (
                <div key={i} style={{ fontSize: 11, color: "var(--color-text-primary, #111)", marginBottom: 3, display: "flex", alignItems: "flex-start", gap: 6 }}>
                  <span style={{ color: cfg.color, marginTop: 1 }}>•</span>{r}
                </div>
              ))}
            </div>
          )}
          <div
            onClick={() => setShow(false)}
            style={{ marginTop: 8, fontSize: 10, color: "var(--color-text-secondary, #6b7280)", cursor: "pointer", textAlign: "right" }}
          >
            Close
          </div>
        </div>
      )}
    </div>
  );
}



const fmt = (n, decimals = 2) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: decimals });

const riskColor = (level) => {
  const map = { LOW: "var(--success)", MODERATE: "var(--warning)", HIGH: "var(--danger)", CRITICAL: "#7c3aed" };
  return map[(level || "").toUpperCase()] || "var(--text-muted)";
};

function AllocationEditor({ item, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    equity_allocated: item.equity_allocated || 0,
    mode: item.mode || "AUTO",
    lot_method: item.lot_method || "EQUITY_RATIO",
  });
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  return (
    <div style={{ background: "rgba(255,255,255,0.98)", borderRadius: 14, border: "1.5px solid #3b82f6", boxShadow: "0 8px 32px rgba(59,130,246,0.12)", padding: "20px 24px", marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "rgba(0,0,0,0.85)" }}>Edit Allocation — {item.name || item.entity_name || "—"}</div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "rgba(0,0,0,0.4)" }}>x</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: "rgba(0,0,0,0.6)", lineHeight: 2 }}>
          <div><span style={{ fontWeight: 700 }}>Type:</span> {item.type || "—"}</div>
          <div><span style={{ fontWeight: 700 }}>AlphaRank:</span> {item.alpha_score ? fmt(item.alpha_score, 1) + " (" + item.grade + ")" : "—"}</div>
          <div><span style={{ fontWeight: 700 }}>Risk:</span> <span style={{ color: riskColor(item.risk_level), fontWeight: 700 }}>{item.risk_level || "—"}</span></div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "rgba(0,0,0,0.5)", marginBottom: 4 }}>EQUITY ALLOCATED (USD)</label>
            <input type="number" className="form-input" value={form.equity_allocated} min={0} onChange={(e) => set("equity_allocated", Number(e.target.value || 0))} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "rgba(0,0,0,0.5)", marginBottom: 4 }}>EXECUTION MODE</label>
            <select className="form-select" value={form.mode} onChange={(e) => set("mode", e.target.value)}>
              <option value="AUTO">AUTO — Automatic execution</option>
              <option value="MANUAL">MANUAL — Alerts only</option>
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "rgba(0,0,0,0.5)", marginBottom: 4 }}>LOT SIZING METHOD</label>
            <select className="form-select" value={form.lot_method} onChange={(e) => set("lot_method", e.target.value)}>
              <option value="EQUITY_RATIO">Equity Ratio (default)</option>
              <option value="FIXED_MULTIPLIER">Fixed Multiplier</option>
              <option value="FIXED_LOT">Fixed Lot</option>
            </select>
            <div style={{ fontSize: 10, color: "rgba(0,0,0,0.4)", marginTop: 4 }}>
              {form.lot_method === "EQUITY_RATIO" && "Lot = trader lot x (your equity / trader equity)"}
              {form.lot_method === "FIXED_MULTIPLIER" && "Lot = trader lot x multiplier value"}
              {form.lot_method === "FIXED_LOT" && "All trades use a fixed lot size"}
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => onSave({ ...item, ...form })}>
          {saving ? "Saving..." : "Save Allocation"}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

const InvestorAllocation = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allocData, setAllocData] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [allocRes, portRes] = await Promise.all([
        api.getInvestorAllocations(),
        api.getInvestorPortfolio(),
      ]);
      setAllocData(allocRes.data || allocRes);
      setPortfolio(portRes.data || portRes);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (updated) => {
    setSaving(true);
    setSaveMsg(null);
    try {
      await api.updateInvestorAllocation({
        entity_id: updated.entity_id,
        entity_type: updated.type,
        equity_allocated: updated.equity_allocated,
        mode: updated.mode,
        lot_method: updated.lot_method,
      });
      setSaveMsg({ type: "success", text: "Allocation saved successfully." });
      setEditor(null);
      await fetchData();
    } catch (err) {
      setSaveMsg({ type: "error", text: "Failed to save: " + err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page"><div style={{ textAlign: "center", padding: "60px 20px", color: "rgba(0,0,0,0.4)", fontSize: 13 }}>Loading allocations...</div></div>;
  if (error) return <div className="page"><div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: "16px 20px", color: "#991b1b", fontSize: 13 }}>Failed to load allocations: {error}</div></div>;

  const allocations   = portfolio?.allocations || [];
  const rebalanceFreq = allocData?.rebalance_frequency || "—";
  const execMode      = allocData?.mode || "AUTO";
  const totalCapital  = portfolio?.total_capital       || 0;
  const allocatedCap  = portfolio?.allocated_capital   || 0;
  const allocPct      = portfolio?.total_allocated_pct || 0;
  const traderAllocs  = allocations.filter((a) => (a.type || "").toUpperCase() === "TRADER");
  const analystAllocs = allocations.filter((a) => (a.type || "").toUpperCase() === "ANALYST");
  const cs = { background: "rgba(255,255,255,0.96)", borderRadius: 14, boxShadow: "0 4px 16px rgba(0,0,0,0.08)", border: "1px solid rgba(0,0,0,0.07)", padding: "18px 20px" };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Investor Allocation</h1>
          <p className="page-subtitle">Configure your capital allocation to traders and analysts. Set execution mode, lot sizing, and equity per strategy.</p>
        </div>
      </div>

      {saveMsg && (
        <div style={{ background: saveMsg.type === "success" ? "#f0fdf4" : "#fef2f2", border: "1px solid " + (saveMsg.type === "success" ? "var(--success)" : "#fecaca"), color: saveMsg.type === "success" ? "#166534" : "#991b1b", borderRadius: 10, padding: "12px 16px", fontSize: 12, fontWeight: 600, marginBottom: 16 }}>
          {saveMsg.text}
        </div>
      )}

      {editor && <AllocationEditor item={editor} onClose={() => setEditor(null)} onSave={handleSave} saving={saving} />}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
        <div style={cs}><div style={{ fontSize: 11, fontWeight: 700, color: "rgba(0,0,0,0.45)", marginBottom: 8 }}>TOTAL CAPITAL</div><div style={{ fontSize: 20, fontWeight: 800 }}>${fmt(totalCapital)}</div></div>
        <div style={cs}><div style={{ fontSize: 11, fontWeight: 700, color: "rgba(0,0,0,0.45)", marginBottom: 8 }}>ALLOCATED</div><div style={{ fontSize: 20, fontWeight: 800 }}>${fmt(allocatedCap)}</div><div style={{ fontSize: 11, color: "rgba(0,0,0,0.4)", marginTop: 4 }}>{fmt(allocPct, 1)}% of total</div></div>
        <div style={cs}><div style={{ fontSize: 11, fontWeight: 700, color: "rgba(0,0,0,0.45)", marginBottom: 8 }}>UNALLOCATED</div><div style={{ fontSize: 20, fontWeight: 800 }}>${fmt(totalCapital - allocatedCap)}</div><div style={{ fontSize: 11, color: "rgba(0,0,0,0.4)", marginTop: 4 }}>Available to deploy</div></div>
        <div style={cs}><div style={{ fontSize: 11, fontWeight: 700, color: "rgba(0,0,0,0.45)", marginBottom: 8 }}>PORTFOLIO MODE</div><div style={{ fontSize: 16, fontWeight: 800 }}>{execMode}</div><div style={{ fontSize: 11, color: "rgba(0,0,0,0.4)", marginTop: 4 }}>Rebalance: {rebalanceFreq}</div></div>
      </div>

      <div style={{ ...cs, marginBottom: 16, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "rgba(0,0,0,0.85)" }}>Trader Allocations</div>
          <div style={{ fontSize: 11, color: "rgba(0,0,0,0.4)", marginTop: 2 }}>Copy-trade to real MT4/MT5 accounts. Performance fee: 20% HWM on auto profits.</div>
        </div>
        {traderAllocs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px 20px", color: "rgba(0,0,0,0.35)", fontSize: 12 }}>No trader allocations yet. Browse the Marketplace to find traders to follow.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table" style={{ margin: 0 }}>
              <thead><tr><th>#</th><th>Trader</th><th>AlphaRank</th><th>Risk</th><th>System Mode</th><th>Mode</th><th>Lot Method</th><th>Equity (USD)</th><th>Return</th><th>Actions</th></tr></thead>
              <tbody>
                {traderAllocs.map((t, idx) => (
                  <tr key={idx}>
                    <td className="text-xs">{idx + 1}</td>
                    <td className="text-xs"><div style={{ fontWeight: 700 }}>{t.name || t.entity_name || "—"}</div><div style={{ fontSize: 10, color: "rgba(0,0,0,0.4)" }}>{t.entity_id || ""}</div></td>
                    <td className="text-xs">{t.alpha_score ? fmt(t.alpha_score, 1) + " (" + t.grade + ")" : "—"}</td>
                    <td className="text-xs"><span style={{ color: riskColor(t.risk_level), fontWeight: 700 }}>{t.risk_level || "—"}</span></td>
                    <td className="text-xs"><Layer3Badge mode={t.layer3_system_mode} reasons={t.layer3_soft_reasons} multiplier={t.layer3_multiplier} /></td>
                    <td className="text-xs">{t.mode || "—"}</td>
                    <td className="text-xs">{(t.lot_method || "—").replace(/_/g, " ")}</td>
                    <td className="text-xs" style={{ fontWeight: 700 }}>${fmt(t.equity_allocated)}</td>
                    <td className="text-xs" style={{ color: (t.return_pct || 0) >= 0 ? "var(--success)" : "var(--danger)", fontWeight: 700 }}>{t.return_pct !== undefined ? ((t.return_pct >= 0 ? "+" : "") + fmt(t.return_pct, 2) + "%") : "—"}</td>
                    <td className="text-xs"><button className="btn btn-xs btn-secondary" onClick={() => setEditor(t)}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ ...cs, marginBottom: 16, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "rgba(0,0,0,0.85)" }}>Analyst Signal Allocations</div>
          <div style={{ fontSize: 11, color: "rgba(0,0,0,0.4)", marginTop: 2 }}>AUTO executes signals automatically. MANUAL sends alerts only.</div>
        </div>
        {analystAllocs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px 20px", color: "rgba(0,0,0,0.35)", fontSize: 12 }}>No analyst allocations yet. Subscribe to an analyst signal set to get started.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table" style={{ margin: 0 }}>
              <thead><tr><th>#</th><th>Analyst</th><th>Signal Set</th><th>AlphaRank</th><th>Risk</th><th>Mode</th><th>Lot Method</th><th>Equity (USD)</th><th>Actions</th></tr></thead>
              <tbody>
                {analystAllocs.map((a, idx) => (
                  <tr key={idx}>
                    <td className="text-xs">{idx + 1}</td>
                    <td className="text-xs" style={{ fontWeight: 700 }}>{a.analyst_name || a.entity_name || "—"}</td>
                    <td className="text-xs">{a.signal_set_name || "—"}</td>
                    <td className="text-xs">{a.alpha_score ? fmt(a.alpha_score, 1) + " (" + a.grade + ")" : "—"}</td>
                    <td className="text-xs"><span style={{ color: riskColor(a.risk_level), fontWeight: 700 }}>{a.risk_level || "—"}</span></td>
                    <td className="text-xs">{a.mode || "—"}</td>
                    <td className="text-xs">{(a.lot_method || "—").replace(/_/g, " ")}</td>
                    <td className="text-xs" style={{ fontWeight: 700 }}>${fmt(a.equity_allocated)}</td>
                    <td className="text-xs"><button className="btn btn-xs btn-secondary" onClick={() => setEditor(a)}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={cs}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "rgba(0,0,0,0.85)", marginBottom: 14 }}>Execution Rules Reference</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 6, color: "rgba(0,0,0,0.8)" }}>Copy Trader</div>
            <ul style={{ margin: 0, paddingLeft: 14, lineHeight: 2 }}>
              <li><strong>Equity Ratio:</strong> lot = trader lot x (your equity / trader equity)</li>
              <li><strong>Fixed Multiplier:</strong> lot = trader lot x multiplier</li>
              <li><strong>Fixed Lot:</strong> fixed lot on all trades — best for micro accounts</li>
            </ul>
          </div>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 6, color: "rgba(0,0,0,0.8)" }}>Analyst Signals</div>
            <ul style={{ margin: 0, paddingLeft: 14, lineHeight: 2 }}>
              <li><strong>AUTO:</strong> executes MARKET/PENDING within validity window</li>
              <li><strong>MANUAL:</strong> alert only — you decide what to execute</li>
              <li><strong>Performance fee:</strong> 10% HWM on auto bucket only</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorAllocation;
